import React from 'react';
import { Button } from '@/components/ui/button';
import { MoreHorizontal, X, Download, Copy, Trash2, Edit } from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';

interface ActionBarProps {
  selectedCount: number;
  onBulkEdit: () => void;
  onDelete: () => void;
  onClearSelection: () => void;
  onExport?: () => void;
  onDuplicate?: () => void;
  disabled?: boolean;
}

const ActionBar: React.FC<ActionBarProps> = ({
  selectedCount,
  onBulkEdit,
  onDelete,
  onClearSelection,
  onExport,
  onDuplicate,
  disabled = false,
}) => {
  if (selectedCount === 0) {
    return null;
  }

  return (
    <div className="flex items-center justify-between rounded-md border bg-muted/40 p-2">
      <div className="flex items-center space-x-2">
        <span className="text-sm font-medium">
          {selectedCount} {selectedCount === 1 ? 'item' : 'items'} selected
        </span>
        <Button
          variant="ghost"
          size="sm"
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            console.log('Clear selection button clicked');
            if (typeof onClearSelection === 'function') {
              onClearSelection();
            }
          }}
          className="h-6 px-2 text-xs text-muted-foreground hover:bg-muted/60"
          disabled={disabled}
        >
          <X className="mr-1 h-3 w-3" />
          Clear
        </Button>
      </div>

      <div className="flex items-center space-x-2">
        <Button
          variant="outline"
          size="sm"
          onClick={onBulkEdit}
          className="h-8 text-xs"
          disabled={disabled}
        >
          <Edit className="mr-1.5 h-3.5 w-3.5" />
          Edit {selectedCount > 1 ? `${selectedCount} items` : 'item'}
        </Button>

        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button
              variant="outline"
              size="sm"
              className="h-8 px-2"
              disabled={disabled}
            >
              <MoreHorizontal className="h-4 w-4" />
              <span className="sr-only">More actions</span>
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-48">
            <DropdownMenuLabel>Actions</DropdownMenuLabel>
            <DropdownMenuSeparator />
            {onExport && (
              <DropdownMenuItem onClick={onExport} disabled={disabled}>
                <Download className="mr-2 h-4 w-4" />
                Export
              </DropdownMenuItem>
            )}
            {onDuplicate && (
              <DropdownMenuItem onClick={onDuplicate} disabled={disabled}>
                <Copy className="mr-2 h-4 w-4" />
                Duplicate
              </DropdownMenuItem>
            )}
            <DropdownMenuItem
              onClick={onDelete}
              className="text-destructive focus:text-destructive"
              disabled={disabled}
            >
              <Trash2 className="mr-2 h-4 w-4" />
              Delete
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
    </div>
  );
};

export default ActionBar;
